import _ from 'lodash';
import React, { SyntheticEvent } from 'react';
import classnames from 'classnames';
import { isLayerType } from 'common/utils/is-layer-type';
import { BBCommonProps, BBEditableModeProps } from 'types/story';
import { COMPONENT_STATES, UNIFORM_PROPS } from 'common/constants';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { SelectionHintEvent } from 'client/components/common/SelectionHint/SelectionHintEvent';
import { TFieldProps, TFieldState } from '../types';
import { FIELD_DATA_ATTR, FIELD_EDIT_MODE, FIELD_EDITABLE_ATTR } from '../constants';
import { Error } from '../Error';
import { withField, placeholderColorPath, placeholderPath, placeholderShowPath } from '../utils';
import css from './TextArea.scss';

type State = TFieldState;

type Props = BBCommonProps & {
	getErrorMsg: (...args: any[]) => any;
};

class TextArea extends React.Component<Props, State> {
	state = {
		editMode: '',
		value: _.get(this.props, placeholderPath),
		readOnly: true,
		pending: false, // pending for a value storing
	};

	static getDerivedStateFromProps(props: Props, state: State) {
		const nextState: Partial<State> = {
			editMode: _.get(props, placeholderShowPath) ? FIELD_EDIT_MODE.PLACEHOLDER : FIELD_EDIT_MODE.VALUE,
		};

		const placeholder = _.get(props, placeholderPath);
		if (placeholder === state.value && state.pending) {
			nextState.value = placeholder;
			nextState.readOnly = true;
			nextState.pending = false;
		}

		// in case of value is updated when edited master symbols from other instance
		if (state.readOnly && placeholder !== state.value) {
			nextState.value = placeholder;
		}

		return nextState;
	}

	get path() {
		return _.get(this.props.editableModeProps, ['nodeProps', 'data-path']);
	}

	getFieldProps() {
		const { name } = this.props.uiConfig.editorProps;
		const placeholder = _.get(this.props, ['uiConfig', 'componentProps', UNIFORM_PROPS.fieldPlaceholder]);
		const color = _.get(this.props, placeholderColorPath);
		const props: Omit<TFieldProps, 'type'> = {
			className: css.area,
			name,
			placeholder,
			autoComplete: 'off',
			[FIELD_DATA_ATTR]: '',
		};

		if (this.props.isEditableMode) {
			props.value = this.state.readOnly ? props.placeholder : this.state.value;
			delete props.placeholder;
			props.readOnly = this.state.readOnly;
			props.onDoubleClick = e => {
				this.setState({ readOnly: false }, () => SelectionHintEvent.triggerFocus(this.props));
			};
			props.onChange = e => {
				this.setState({ value: e.target.value });
			};
			props.onBlur = this.onEditableBlur;

			if (this.state.editMode === FIELD_EDIT_MODE.PLACEHOLDER) {
				_.set(props, `style.color`, color);
			} else {
				_.set(props, `style.color`, '');
			}

			if (!props.readOnly) {
				props[FIELD_EDITABLE_ATTR] = ''; // needed to control component drag
			}

			props.onFocus = (event: SyntheticEvent) => {
				// prevent this block from being selected when clicking on its parent
				event.stopPropagation();
			};
		}

		return props;
	}

	onEditableBlur = () => {
		if (this.state.value === _.get(this.props, placeholderPath)) {
			this.setState({ readOnly: true });
			return;
		}

		transmitToAdminBbUpdate({
			id: TextArea.name,
			path: `elements.${this.path}.${placeholderPath}`,
			value: this.state.value,
			isStory: isLayerType(this.props).global,
		});

		this.setState({ pending: true });
	};

	renderEditable() {
		const props = { ...this.props.editableModeProps?.nodeProps, className: css.editable };
		return this.renderDefault(props);
	}

	renderDefault(props: Partial<BBEditableModeProps['nodeProps']> = {}) {
		const { nodeProps, editorProps } = this.props.uiConfig;
		const className = classnames(css.textArea, nodeProps.className, props.className);
		const isIncorrect = _.has(props, 'data-incorrect-state') || this.props.states[COMPONENT_STATES.INCORRECT];
		const errorMessage = this.props.getErrorMsg(props);

		const common = {
			...this.props.uiConfig.nodeProps,
			...this.props.stateAttrs,
			...this.props.eventListeners,
		};

		return (
			<div {...common} {...props} className={className} ref={this.props.containerRef}>
				<div className={css.areaWrap}>
					<textarea {...this.getFieldProps()} />
				</div>
				<Error
					isHidden={!isIncorrect || typeof errorMessage === 'undefined'}
					path={this.path}
					isEditableMode={!!this.props.isEditableMode}
					errorType={editorProps[UNIFORM_PROPS.fieldErrorShow]}
					text={errorMessage}
				/>
			</div>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

export default withCardTransitionContext(withField(TextArea));
